import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Gauge, MapPin, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button'; // 使用 shadcn/ui 的 button
import { buildProductPath, Product } from '../../data/productsLoader';

interface ProductCardProps {
  product: Product;
  onAddToCart?: (product: Product) => void;
  onToggleFavorite?: (productId: string) => void;
  isFavorite?: boolean;
}

// 生成产品图片数组
function getProductImages(product: Product): string[] {
  // 优先使用 product.images（来自 products.json）
  if (product.images && product.images.length > 0) {
    return product.images;
  }

  // 兼容旧逻辑：如果没有 images 字段，则自动生成
  const baseImage = product.image;
  const images: string[] = [baseImage];

  // 根据产品 ID 推测可能的图片数量
  const imageCountMap: Record<string, number> = {
    'zfox-max': 11,
    'zfox-plus': 8,
    'zlion-pro': 8,
    'zlion-x': 6,
    'zwolf': 6,
    'zwolf-plus': 6,
    'zwolf-duo': 6,
    'zcougar': 6,
    'zcougar-plus': 6,
    'zcougar-duo': 6,
    'zrino-se': 5,
    'zrino-duo': 6,
    'zrino-duo-max': 6
  };

  const maxImages = imageCountMap[product.id] || 1;
  const basePath = baseImage.replace(/\/\d+\.webp$/, '');

  for (let i = 2; i <= maxImages; i++) {
    images.push(`${basePath}/${i}.webp`);
  }

  return images;
}

export function ProductCard({
  product,
  onToggleFavorite,
  isFavorite = false
}: ProductCardProps) {
  const { id, name, specs, slogan } = product;
  const isAvailable = true; // 所有产品默认可用
  const shopUrl = product.shopUrl;
  const productPath = product.link || buildProductPath(product.lineId, id);

  // 图片轮播状态
  const productImages = getProductImages(product);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [direction, setDirection] = useState(0); // 1 = 下一张, -1 = 上一张

  // 自动轮播（悬停时启动）
  useEffect(() => {
    if (!isHovering || productImages.length <= 1) return;

    const interval = setInterval(() => {
      setDirection(1);
      setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [isHovering, productImages.length]);

  const handlePrevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDirection(-1);
    setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDirection(1);
    setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
  };

  return (
    <motion.div
      className="group bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col h-full"
      whileHover={{ y: -5 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Image Container with Carousel */}
      <div className="relative aspect-square overflow-hidden bg-gray-50">
        <a href={shopUrl} target="_blank" rel="noopener noreferrer">
          <AnimatePresence initial={false} mode="wait" custom={direction}>
            <motion.img
              key={currentImageIndex}
              src={productImages[currentImageIndex] || '/placeholder-scooter.png'}
              alt={`${name} - ${currentImageIndex + 1}`}
              className="w-full h-full object-contain p-4"
              custom={direction}
              variants={{
                enter: (direction: number) => ({
                  x: direction > 0 ? 100 : -100,
                  opacity: 0,
                  scale: 0.95
                }),
                center: {
                  x: 0,
                  opacity: 1,
                  scale: 1
                },
                exit: (direction: number) => ({
                  x: direction > 0 ? -100 : 100,
                  opacity: 0,
                  scale: 0.95
                })
              }}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.3 },
                scale: { duration: 0.3 }
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/placeholder-scooter.png';
              }}
            />
          </AnimatePresence>
        </a>

        {/* Carousel Navigation - Only show if multiple images */}
        {productImages.length > 1 && (
          <>
            {/* Previous Button */}
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity z-10"
            >
              <ChevronLeft className="w-4 h-4 text-gray-700" />
            </button>

            {/* Next Button */}
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity z-10"
            >
              <ChevronRight className="w-4 h-4 text-gray-700" />
            </button>

            {/* Image Indicators */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <div className="flex gap-2 bg-black/50 backdrop-blur-sm px-3 py-2 rounded-full">
                {productImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCurrentImageIndex(idx);
                    }}
                    className={`rounded-full transition-all ${
                      idx === currentImageIndex
                        ? 'bg-white w-8 h-2 shadow-lg'
                        : 'bg-white/50 w-2 h-2 hover:bg-white/80 hover:scale-125'
                    }`}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {/* Favorite Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            onToggleFavorite?.(id);
          }}
          className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
        >
          <Heart
            className={`h-5 w-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-current'}`}
          />
        </button>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {!isAvailable && (
            <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-sm">
              Agotado
            </span>
          )}
          {id.includes('zwolf') && (
            <span className="bg-black text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-sm">
              Off-Road
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-grow">
        <div className="mb-4">
          <a href={shopUrl} target="_blank" rel="noopener noreferrer" className="block">
            <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary transition-colors mb-1">
              {name}
            </h3>
          </a>
          {slogan && (
            <p className="text-sm text-gray-500 font-medium line-clamp-2">
              {slogan}
            </p>
          )}
        </div>

        {/* Quick Specs Grid */}
        <div className="grid grid-cols-3 gap-2 mb-6 py-3 border-t border-b border-gray-50">
          <div className="text-center">
            <Zap className="w-4 h-4 mx-auto mb-1 text-yellow-500" />
            <span className="block text-xs font-bold text-gray-900">{specs.motor}</span>
            <span className="text-[10px] text-gray-400 uppercase">Potencia</span>
          </div>
          <div className="text-center border-l border-gray-100">
            <Gauge className="w-4 h-4 mx-auto mb-1 text-blue-500" />
            <span className="block text-xs font-bold text-gray-900">{specs.maxSpeed}</span>
            <span className="text-[10px] text-gray-400 uppercase">Velocidad</span>
          </div>
          <div className="text-center border-l border-gray-100">
            <MapPin className="w-4 h-4 mx-auto mb-1 text-green-500" />
            <span className="block text-xs font-bold text-gray-900">{specs.range}</span>
            <span className="text-[10px] text-gray-400 uppercase">Autonomía</span>
          </div>
        </div>

        <div className="mt-auto flex gap-2">
            <Link to={productPath} className="flex-1">
              <Button variant="outline" size="sm" className="w-full text-xs font-medium">
                Ficha tecnica
              </Button>
            </Link>
            {isAvailable && (
              <Button asChild size="sm" className="flex-1 bg-black hover:bg-gray-800 text-white">
                <a href={shopUrl} target="_blank" rel="noopener noreferrer">
                  Ver producto
                </a>
              </Button>
            )}
        </div>
      </div>
    </motion.div>
  );
}
